import type { ComponentType } from "react";
import type { LucideProps } from "lucide-react";
import {
  Activity,
  Bell,
  Box,
  Boxes,
  Briefcase,
  Building,
  Building2,
  Calendar,
  Car,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Command,
  CreditCard,
  Cpu,
  File,
  FileText,
  Folder,
  FolderOpen,
  Globe,
  HardDrive,
  HelpCircle,
  Home,
  Key,
  Layers,
  LayoutDashboard,
  Lock,
  LogOut,
  Map as MapIcon,
  Menu,
  MessageSquare,
  Package,
  Radio,
  ReceiptText,
  Settings,
  Shield,
  ShieldCheck,
  Sliders,
  Smartphone,
  Truck,
  User,
  UserCheck,
  Users,
  Wrench,
  X,
} from "lucide-react";
import { PointOnMapLinearIcon } from "@nasam/ui";

export type IconComponent = ComponentType<LucideProps>;

export function FallbackIcon(props: LucideProps) {
  return <HelpCircle data-testid="fallback-icon" {...props} />;
}

/** The Fleet rail item's icon (Figma: a pin on a map), from Solar; the nav
 *  only ever sizes and colours its icons through `className`. */
function PointOnMapIcon({ className }: LucideProps) {
  return <PointOnMapLinearIcon className={className} />;
}

export const DEFAULT_FALLBACK_ICON_NAME = "HelpCircle";
export const DEFAULT_FALLBACK_ICON: IconComponent = FallbackIcon;

function normalizeIconName(name: string): string {
  return name.toLowerCase().replace(/[-_\s]/g, "");
}

const defaultIcons: Readonly<Record<string, IconComponent>> = {
  activity: Activity,
  bell: Bell,
  box: Box,
  boxes: Boxes,
  briefcase: Briefcase,
  building: Building,
  building2: Building2,
  calendar: Calendar,
  car: Car,
  checksquare: CheckSquare,
  chevrondown: ChevronDown,
  chevronleft: ChevronLeft,
  chevronright: ChevronRight,
  chevronup: ChevronUp,
  circlehelp: FallbackIcon,
  command: Command,
  creditcard: CreditCard,
  cpu: Cpu,
  file: File,
  filetext: FileText,
  folder: Folder,
  folderopen: FolderOpen,
  globe: Globe,
  harddrive: HardDrive,
  helpcircle: FallbackIcon,
  home: Home,
  key: Key,
  layers: Layers,
  layoutdashboard: LayoutDashboard,
  lock: Lock,
  logout: LogOut,
  map: MapIcon,
  menu: Menu,
  messagesquare: MessageSquare,
  package: Package,
  pointonmap: PointOnMapIcon,
  radio: Radio,
  receipttext: ReceiptText,
  settings: Settings,
  shield: Shield,
  shieldcheck: ShieldCheck,
  sliders: Sliders,
  smartphone: Smartphone,
  truck: Truck,
  user: User,
  usercheck: UserCheck,
  users: Users,
  wrench: Wrench,
  x: X,
};

const customIcons = new Map<string, IconComponent>();

export function resolveIcon(name?: string): IconComponent {
  if (name === undefined || name.trim() === "") {
    return DEFAULT_FALLBACK_ICON;
  }

  const key = normalizeIconName(name);
  const custom = customIcons.get(key);
  if (custom !== undefined) {
    return custom;
  }

  const defaultIcon = defaultIcons[key];
  if (defaultIcon !== undefined) {
    return defaultIcon;
  }

  if (import.meta.env.DEV) {
    console.warn(
      `[iconRegistry] Icon "${name}" not found; rendering fallback icon (${DEFAULT_FALLBACK_ICON_NAME}).`,
    );
  }

  return DEFAULT_FALLBACK_ICON;
}

export function registerIcon(name: string, component: IconComponent): void {
  customIcons.set(normalizeIconName(name), component);
}

export function clearCustomIcons(): void {
  customIcons.clear();
}
